import { cartServiceInstance } from './api.js';

export let isUpdatingCartDrawer = false;
export let sectionsToFetch = 'cart-drawer';

document.addEventListener('on:cart:loaded', (event) => {
  if (event.detail.type === 'ADD') {
    document.querySelector('.js-cart-drawer')?.scrollTo(0, 0, { behavior: 'smooth'})

    fetchCartDrawer(true);
  }
});

/**
 * Disables cart interaction
 */
export function disableCartInteraction() {
  const cartDrawer = document.querySelector('.js-cart-drawer');

  if (!!(cartDrawer)) {
    cartDrawer.setAttribute('data-is-updating', 'true');
  }
}

/**
 * Enables cart interaction
 */
export function enableCartInteraction() {
  const cartDrawer = document.querySelector('.js-cart-drawer');

  if (!!(cartDrawer)) {
    cartDrawer.removeAttribute('data-is-updating');
  }
}

/**
 * Applies a cart mutation response to the drawer UI with a single refresh path.
 *
 * @param {Object} data - Cart response payload
 * @param {Boolean} showCartDrawer - Whether to open drawer modal
 */
function refreshDrawerFromCartMutation(data, showCartDrawer = true) {
  if (data && typeof data === 'object' && data.sections && typeof data.sections === 'object' && data.sections.hasOwnProperty('cart-drawer')) {
    updateCartDrawer(data, showCartDrawer);
    return;
  }

  fetchCartDrawer(showCartDrawer);
}

/**
 * Fetches the cart drawer content and updates the cart drawer
 * 
 * @param {Boolean} showCartDrawer - Whether to show the cart drawer
 * @param {Boolean} isShopPay - Whether the user is using Shop Pay
 * @param {Boolean} straightToCheckout - Whether the user is going straight to checkout
 */

export function fetchCartDrawer(showCartDrawer = false, isShopPay = false, straightToCheckout = false) {
  if (isShopPay) {
    window.location.href = '/checkout?payment=shop_pay';
  } else if (straightToCheckout) {
    window.location.href = '/checkout?skip_shop_pay=true';
  } else if (!isUpdatingCartDrawer) {
    isUpdatingCartDrawer = true;

    const handleFetchCartDrawerError = (error) => {
      console.error(error);

      // Clear the in-flight lock so future cart drawer refreshes are not blocked after a fetch failure.
      isUpdatingCartDrawer = false;
      enableCartInteraction();
    };

    cartServiceInstance.fetchCartSections(sectionsToFetch)
      .then((data) => {
        const sections = data;

        if (!sections || typeof sections !== 'object' || !sections.hasOwnProperty('cart-drawer')) {
          throw new Error('Invalid cart section response. Skipping cart drawer update.');
        }

        // Updates the cart drawer
        cartServiceInstance.fetchCart()
          .then((cartData) => {
            if (!cartData || typeof cartData !== 'object') {
              throw new Error('Invalid cart data response. Skipping cart drawer update.');
            }

            cartData['sections'] = sections;
            if (cartData.attributes.hasOwnProperty('error_discount_codes')) {
              cartServiceInstance.updateCart({
                attributes: {
                  error_discount_codes: '',
                },
                sections: sectionsToFetch,
              })
                .then((updatedData) => {
                  refreshDrawerFromCartMutation(updatedData, showCartDrawer);
                })
                .catch(handleFetchCartDrawerError);

              return;
            }

            updateCartDrawer(cartData, showCartDrawer);
          })
          .catch(handleFetchCartDrawerError);
      })
      .catch(handleFetchCartDrawerError);
    }
}

/**
 * Updates the cart drawer
 *
 * @param {Object} data - The data to update the cart drawer with
 * @param {Boolean} showCartDrawer - Whether to show the cart drawer
 */
export async function updateCartDrawer(data, showCartDrawer = false) {
  try {
    if (!!(data)) {
      if (!data.sections || typeof data.sections !== 'object' || !data.sections.hasOwnProperty('cart-drawer')) {
        fetchCartDrawer(showCartDrawer);
        return;
      }

      const cartDrawer = document.querySelector('.js-cart-drawer');
      const cartModal = document.querySelector('.modal--cart');

      if (!!(cartDrawer)) {
        const cartCountBubble = document.querySelectorAll('.cart-count-bubble items-count');
        const cartDrawerHTML = new DOMParser().parseFromString(data?.sections['cart-drawer'], 'text/html');
        const cartDrawerContainer = cartDrawer.querySelector('.js-cart--draw');
        const cartDrawerFreeShippingBar = cartDrawer.querySelector('.js-cart-draw__free-shipping-bar');
        const cartDrawerMarketingMessage = cartDrawer.querySelector('.js-cart-draw__notice');
        const cartQuantityInfo = cartDrawer.querySelector('.js-cart-draw__quantity-info items-count');
        let cartControls = cartDrawer.querySelector('.js-cart__controls');

        if (Object.keys(data?.sections).length > 1) {
          for (const section of Object.entries(data?.sections)) {
            if (section[0] !== 'cart-drawer') {
              const sectionHTML = new DOMParser().parseFromString(section[1], 'text/html');
              const sectionContainer = document.getElementById(`shopify-section-${section[0]}`);

              if (!!(sectionContainer)) {
                sectionContainer.innerHTML = sectionHTML.querySelector('.shopify-section')?.innerHTML;
              }
            }
          }
        }

        if (!!(cartDrawerContainer)) {
          cartDrawerContainer.innerHTML = cartDrawerHTML.querySelector('.js-cart--draw')?.innerHTML;
        }

        const cartDrawerContent = cartDrawer.querySelector('.js-cart__contents');

        if (!!(cartQuantityInfo)) {
          if (!!(cartDrawerContent)) {
            cartQuantityInfo.innerHTML = cartDrawerHTML.querySelector('.js-cart-draw__quantity-info items-count')?.innerHTML;
          } else {
            cartQuantityInfo.innerHTML = '0';
          }
        }

        if (!!(cartCountBubble) && cartCountBubble.length > 0) {
          cartCountBubble.forEach((countBubble) => {
            if (!!(cartDrawerContent)) {
              countBubble.innerHTML = cartDrawerHTML.querySelector('.js-cart-draw__quantity-info items-count')?.innerHTML;
            } else {
              countBubble.innerHTML = '0';
            }
          });
        }

        if (!!(cartDrawerFreeShippingBar)) {
          cartDrawerFreeShippingBar.innerHTML = cartDrawerHTML.querySelector('.js-cart-draw__free-shipping-bar')?.innerHTML;
        }

        if (!!(cartDrawerMarketingMessage)) {
          cartDrawerMarketingMessage.innerHTML = cartDrawerHTML.querySelector('.js-cart-draw__notice')?.innerHTML;
        }

        if (!!cartModal) {
          if (!cartModal.shown && showCartDrawer) {
            cartModal.open();
          } else if (cartModal.interval) {
            cartModal.interval.reset();
            cartModal.interval.start();
          }
        }
        
        if (data.item_count > 0) {
          const cartItems = data?.items;

          if (cartItems.length > 0) {
            if (!cartDrawerContainer.querySelector('[data-enable-searchspring="true"]')) {
              const newRecommendations = {};
              const outputRecommendations = [];
              const productIds = cartItems.reduce(((ids, item) => {
                if (!item.gift_card && !ids.includes(item.product_id.toString())) ids.push(item.product_id.toString());
                return ids;
              }), []);
              let recommendationsLimit = 6;

              if (productIds.length >= 3) {
                recommendationsLimit = 2;
              } else if (productIds.length >= 2) {
                recommendationsLimit = 4;
              }

              await Promise.all([...productIds].map((async productId => {
                const response = await fetch(`${window.Shopify.routes.root}recommendations/products?section_id=cart-drawer-recommendations&product_id=${productId}&limit=${recommendationsLimit}&intent=related`);
                const recommendationsHTML = await response.text();
                const recommendationsJSON = new DOMParser().parseFromString(recommendationsHTML, 'text/html').querySelector('.shopify-section').textContent.trim();

                let recommendations = {};
                let qaIndex = 0;

                // Remove trailing commas from JSON text
                const recommendationsJSONClean = recommendationsJSON
                  .replace(/,\s*([}\]])/g, '$1');

                if (recommendationsJSONClean.startsWith('{') && recommendationsJSONClean.endsWith('}')) {
                  try {
                    recommendations = JSON.parse(recommendationsJSONClean);
                  } catch {
                    console.error('Could not parse recommendations JSON', recommendationsJSONClean);
                  }
                }

                if (recommendations?.products) {
                  Object.entries(recommendations?.products)?.forEach(([key, value]) => {
                    if (!Object.keys(newRecommendations)?.includes(key.toString())) {
                      const updatedHTML = value.replaceAll('data-qa=""', `data-qa="quick-add-${key}"`);
                      qaIndex += 1;

                      outputRecommendations.push(updatedHTML);
                    }

                    newRecommendations[key.toString()] = true;
                  });

                  if (outputRecommendations.length <= recommendationsLimit) {
                    recommendationsLimit = 2;
                  }
                }
              })));

              const cartRecommendations = cartDrawerContainer.querySelector('.js-cart-recommendations');

              if (!!(cartRecommendations)) {
                if (cartRecommendations.innerHTML !== outputRecommendations.slice(0, 6).join('')) {
                  cartRecommendations.innerHTML = outputRecommendations.slice(0, 6).join('');
                }
              }
            } else {
              const recommendationsScript = cartDrawerContainer.querySelector('script[data-section-id="drawer-cart-recommendations"]');

              if (!!(recommendationsScript)) {
                const tag = recommendationsScript.dataset.tag;
                const limit = recommendationsScript.dataset.limit;
                const placement = recommendationsScript.dataset.placement;
                const recommendationsUrl = recommendationsScript.dataset.recommendationsUrl;
                const cartProductIds = recommendationsScript.dataset.cartProductIds;
                let shopper = recommendationsScript.dataset.shopper;

                if (shopper === undefined) {
                  shopper = document.cookie.split(';').find(row => row.trim().startsWith('ssShopperId='))?.split('=')[1];
                }

                var storedProducts = document.cookie.split(';').find(row => row.trim().startsWith('recentlyViewedProducts='))?.split('=')[1];
                var viewedProducts = [];
                var viewedProductIds = [];

                if (storedProducts) {
                  viewedProducts = storedProducts.split(',');

                  viewedProducts.forEach((productIdentifier) => {
                    viewedProductIds.push(productIdentifier.split(':')[1]);
                  });
                }

                fetch(`${recommendationsUrl.replace('REPLACE_TAG', tag)}&cart=${cartProductIds}&lastViewed=${viewedProductIds.join(',')}${(shopper !== undefined && shopper !== '') ? '&shopper=' + shopper : ''}`,
                  {
                    method: 'GET',
                    headers: {
                      accept: 'application/json'
                    }
                  })
                  .then((response) => response.json())
                  .then((data) => {
                    const productCardSection = document.querySelector('[data-section-id="drawer-cart-recommendations"] .grid-layout');

                    initRecommendationsSection(productCardSection, tag, data, limit, placement);
                  })
                  .catch((error) => console.error(error));
              }
            }
          }
        }

        cartControls = cartDrawer.querySelector('.js-cart__controls');

        if (!!(cartControls) && !!(cartDrawer)) {
          cartDrawer.parentElement.style.setProperty('--cart-drawer-footer-height', `${cartControls.offsetHeight}px`);
        } else if (!!(cartDrawer)) {
          cartDrawer.parentElement.style.setProperty('--cart-drawer-footer-height', `0px`);
        }
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    isUpdatingCartDrawer = false;

    enableCartInteraction();
  }
}

/**
 * Adds an item to the cart
 *
 * @param {FormData} formData - The form data to add the item to the cart with
 * @param {Boolean} isShopPay - Whether or not Shop Pay accelerated checkout
 *                              button was used to add the product and
 *                              therefore should have the user redirected to
 *                              checkout using Shop Pay.
 * @param {Boolean} straightToCheckout - Whether or not it should go straight
 *                                       to checkout.
 */
export function cartAdd(formData, isShopPay = false, straightToCheckout = false) {
  if (formData instanceof FormData && !formData.has('sections')) {
    formData.append('sections', sectionsToFetch);
  }

  cartServiceInstance.addToCart(formData)
    .then((data) => {
      if (isShopPay || straightToCheckout) {
        fetchCartDrawer(true, isShopPay, straightToCheckout);
        return;
      }

      refreshDrawerFromCartMutation(data, true);
    })
    .catch((error) => {
      console.error(error);

      enableCartInteraction();
    });
}

/**
 * Changes line item quantity
 *
 * @param {Object} change - The item to update
 * @param {Boolean} updateDrawer - Whether to update the cart drawer
 */
export function cartChange(change, updateDrawer = true) {
  cartServiceInstance.changeCart(change)
    .then((data) => {
      if (updateDrawer) {
        refreshDrawerFromCartMutation(data, true);
      }
    })
    .catch((error) => {
      console.error(error);

      enableCartInteraction();
    });
}

/**
 * Updates line items
 *
 * @param {Object} updates - The items to update
 * @param {Boolean} updateDrawer - Whether to update the cart drawer
 */
export function cartUpdate(updates, updateDrawer = true) {
  cartServiceInstance.updateCart(updates)
    .then((data) => {
      if (updateDrawer) {
        refreshDrawerFromCartMutation(data, true);
      }
    })
    .catch((error) => {
      console.error(error);

      enableCartInteraction();
    });
}

export function initializeCartHandlers() {
  const cartControls = document.querySelector('.js-cart__controls');
  const cartDrawer = document.querySelector('.js-cart-drawer');
  const cartItemRemoves = document.querySelectorAll('.js-cart-item__remove');
  const cartModal = document.querySelector('.modal--cart');
  const quantityInputs = document.querySelectorAll('.js-cart-quantity-input');

  fetchCartDrawer(false);

  document.querySelectorAll('.shopify-section-group-gwp')?.forEach((section) => {
    sectionsToFetch += `,${section.id.replace('shopify-section-', '')}`;
  });

  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams.has('cart') && urlParams.get('cart') === 'true') {
    if (!!cartModal) {
      if (!cartModal.shown) {
        cartModal.open();
      } else if (cartModal.interval) {
        cartModal.interval.reset();
        cartModal.interval.start();
      }
    }
  }

  if (urlParams.has('pc')) {
    const promoCode = urlParams.get('pc');
    const sentDiscountCodes = [];

    sentDiscountCodes.push(promoCode);

    cartServiceInstance.updateCart({
        discount: promoCode
      })
        .then((data) => {
          let appliedDiscountCode = '';
          let errorDiscountCode = '';
          let foundDiscountCodes = [];

          if (data.hasOwnProperty('discount_codes') && Object.values(data.discount_codes).length > 0) {
            foundDiscountCodes = Object.values(data.discount_codes || {})
              .filter(d => d.applicable)
              .map(d => d.code);
            appliedDiscountCode = foundDiscountCodes.join(',');
          }

          sentDiscountCodes.forEach((sentDiscountCode) => {
            if (!foundDiscountCodes.includes(sentDiscountCode)) {
              errorDiscountCode += `${sentDiscountCode},`;
            }
          });

          if (errorDiscountCode !== '') {
            errorDiscountCode = errorDiscountCode.slice(0, -1);
          }

          return cartServiceInstance.updateCart({
            attributes: {
              applied_discount_codes: appliedDiscountCode,
              error_discount_codes: errorDiscountCode,
            },
            sections: sectionsToFetch,
          });
        })
        .then((data) => {
          refreshDrawerFromCartMutation(data, false);
        })
        .catch((error) => {
          console.error(error);
        });
  }

  if (!!(cartItemRemoves) && cartItemRemoves.length > 0) {
    cartItemRemoves.forEach((cartItemRemove) => {
      cartItemRemove.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        disableCartInteraction();

        cartChange({
          id: event.currentTarget.dataset.key,
          quantity: 0,
          sections: sectionsToFetch
        });
      });
    });
  }

  if (!!(quantityInputs) && quantityInputs.length > 0) {
    quantityInputs.forEach((input) => {
      input.addEventListener('update', (event) => {
        event.stopImmediatePropagation();

        disableCartInteraction();

        const updates = {};

        updates[event.detail.key] = Number(event.detail.value);

        cartUpdate({
          updates,
          sections: sectionsToFetch
        });
      });
    });
  }

  if (!!(cartControls) && !!(cartDrawer)) {
    cartDrawer.parentElement.style.setProperty('--cart-drawer-footer-height', `${cartControls.offsetHeight}px`);
  }

  const cartObserver = new MutationObserver((mutations, observer) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const cartItemRemoves = node.querySelectorAll('.js-cart-item__remove');
          const quantityInputs = node.querySelectorAll('.js-cart-quantity-input');

          if (!!(cartItemRemoves) && cartItemRemoves.length > 0) {
            cartItemRemoves.forEach((cartItemRemove) => {
              cartItemRemove.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();

                disableCartInteraction();

                cartChange({
                  id: event.currentTarget.dataset.key,
                  quantity: 0,
                  sections: sectionsToFetch
                });
              });
            });
          }

          if (!!(quantityInputs) && quantityInputs.length > 0) {
            quantityInputs.forEach((input) => {
              input.addEventListener('update', async (event) => {
                event.stopImmediatePropagation();

                disableCartInteraction();

                const updates = {};

                updates[event.detail.key] = Number(event.detail.value);

                cartUpdate({
                  updates,
                  sections: sectionsToFetch,
                });
              });
            });
          }
        }
      });
    });
  });

  cartObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  /**
   * Gift with Purchase
   */

  let gwpSections = document.querySelectorAll('.shopify-section-group-gwp');
  let gwpItems = document.querySelectorAll('[name="gwp"]');
  let gwpUpdates = [];

  const queueGwpAdd = (addData) => {
    gwpUpdates.push(cartServiceInstance.addToCart(addData));
  };

  const queueGwpUpdate = (updateData, includeSections = false) => {
    gwpUpdates.push(cartServiceInstance.updateCart({
      ...updateData,
      ...(includeSections ? { sections: sectionsToFetch } : {}),
    }));
  };

  if (!!(gwpItems) && gwpItems.length > 0) {
    gwpItems.forEach((gwp) => {
      if (gwp.dataset.type === 'add' && gwp.value.startsWith('{')) {
        const data = JSON.parse(gwp.value);
        const uuid = window.crypto.randomUUID();

        if (data.hasOwnProperty('add')) {
          data.add.properties['_uuid'] = uuid;

          queueGwpAdd(data.add);
        }

        gwpUpdates.push(new Promise((resolve) => {
          setTimeout(resolve, 1000);
        }).then(() => cartServiceInstance.updateCart({
          ...data.update,
          sections: sectionsToFetch,
        })));

        gwp.remove();
      } else if (gwp.dataset.type === 'remove' && gwp.value.startsWith('{')) {
        queueGwpUpdate(JSON.parse(gwp.value), true);

        gwp.remove();
      }
    });

    Promise.all(gwpUpdates)
      .then((results) => {
        const latestResult = (Array.isArray(results) && results.length > 0) ? results[results.length - 1] : null;

        if (latestResult) {
          refreshDrawerFromCartMutation(latestResult, false);
        } else {
          fetchCartDrawer(false);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  const gwpObserver = new MutationObserver((mutations, observer) => {
    mutations.forEach((mutation) => {
      gwpUpdates = [];

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          gwpSections = document.querySelectorAll('.shopify-section-group-gwp');

          if (!!(gwpSections) && gwpSections.length > 0) {
            gwpSections.forEach((section) => {
              if (!section.hasAttribute('data-is-being-observed')) {
                gwpObserver.observe(section, {
                  childList: true,
                  subtree: true
                });

                section.setAttribute('data-is-being-observed', 'true');
              }
            });
          }

          if (node.dataset.type === 'add' && node.value.startsWith('{')) {
            const data = JSON.parse(node.value);
            const uuid = window.crypto.randomUUID();

            if (data.hasOwnProperty('add')) {
              data.add.properties['_uuid'] = uuid;

              queueGwpAdd(data.add);
            }

            gwpUpdates.push(new Promise((resolve) => {
              setTimeout(resolve, 1000);
            }).then(() => cartServiceInstance.updateCart({
              ...data.update,
              sections: sectionsToFetch,
            })));

            node.remove();
          } else if (node.dataset.type === 'remove' && node.value.startsWith('{')) {
            queueGwpUpdate(JSON.parse(node.value), true);

            node.remove();
          }
        }
      });

      Promise.all(gwpUpdates)
        .then((results) => {
          const latestResult = (Array.isArray(results) && results.length > 0) ? results[results.length - 1] : null;

          if (latestResult) {
            refreshDrawerFromCartMutation(latestResult, false);
          } else {
            fetchCartDrawer(false);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    });
  });

  if (!!(gwpSections) && gwpSections.length > 0) {
    gwpSections.forEach((section) => {
      gwpObserver.observe(section, {
        childList: true,
        subtree: true
      });

      section.setAttribute('data-is-being-observed', 'true');
    });
  }
}

// Expose mutable exported values globally with live getter/setter bindings.
Object.defineProperties(window, {
  isUpdatingCartDrawer: {
    configurable: true,
    get: () => isUpdatingCartDrawer,
    set: (value) => {
      isUpdatingCartDrawer = value;
    },
  },
  sectionsToFetch: {
    configurable: true,
    get: () => sectionsToFetch,
    set: (value) => {
      sectionsToFetch = value;
    },
  },
});

// Expose exported cart drawer functions globally for legacy/theme script access.
Object.assign(window, {
  disableCartInteraction,
  enableCartInteraction,
  fetchCartDrawer,
  updateCartDrawer,
  cartAdd,
  cartChange,
  cartUpdate,
  initializeCartHandlers,
});

if(['interactive', 'complete'].includes(document.readyState)){
  initializeCartHandlers();
} else {
  document.addEventListener('DOMContentLoaded', initializeCartHandlers);
}

/**
 * Item Personalization
 */

if (!customElements.get('item-personalization')) {
  customElements.define('item-personalization', class ItemPersonalization extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.editButton = this.querySelector('.js-cart-item__personalization__header__edit-button');
      this.formInputs = this.querySelectorAll('input');
      this.saveButton = this.querySelector('.js-cart-item__personalization__edit-form__save-button');

      this.initialProperties = {};

      if (!!(this.formInputs) && this.formInputs.length > 0) {
        this.formInputs.forEach((input) => {
          this.initialProperties[input.name] = input.value;
        });
      }

      this.editButton.addEventListener('click', this.handleEditButtonClick.bind(this));
      this.saveButton.addEventListener('click', this.handleSaveButtonClick.bind(this));
    }

    /**
     * Handles the edit button click
     *
     * @param {Event} event - The event object
     */
    handleEditButtonClick(event) {
      this.toggleAttribute('data-is-editing');
    }

    /**
     * Handles the save button click
     *
     * @param {Event} event - The event object
     */
    handleSaveButtonClick(event) {
      if (!!(this.formInputs) && this.formInputs.length > 0) {
        const currentProperties = {};
        const changeData = {
          id: this.dataset.itemKey,
          quantity: Number(this.dataset.itemQuantity),
        };
        let hasProperties = false;

        disableCartInteraction();

        this.formInputs.forEach((input) => {
          currentProperties[input.name] = input.value;
        });

        if (JSON.stringify(currentProperties) === JSON.stringify(this.initialProperties)) {
          this.toggleAttribute('data-is-editing');

          return;
        }

        this.formInputs.forEach((input) => {
          if (!hasProperties && input.name.startsWith('properties[') && !input.name.startsWith('properties[_')) {
            hasProperties = true;

            changeData['properties'] = {};
          }

          if (input.name === 'properties[_personalization]') {
            const personalizationFormData = [];

            this.formInputs.forEach((input) => {
              if (input.hasAttribute('data-key')) {
                personalizationFormData.push({
                  key: input.dataset.key,
                  value: input.value,
                });
              }
            });

            input.value = JSON.stringify(personalizationFormData);
          }

          changeData['properties'][input.name.replace('properties[', '').replace(']', '')] = input.value;
        });

        cartServiceInstance.changeCart({
            ...changeData,
            sections: sectionsToFetch,
          })
          .then((data) => {
            refreshDrawerFromCartMutation(data, true);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.removeAttribute('data-is-editing');
          });
      }
    }
  });
}

/**
 * Accelerated Checkout Buttons
 */

if (!customElements.get('accelerated-checkout-buttons')) {
  customElements.define('accelerated-checkout-buttons', class AcceleratedCheckoutButtons extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.buttons = this.querySelectorAll('.dynamic-button');
      this.component = this.dataset.component;

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.handleClick.bind(this));
      });

      // Checks if the device is an Apple device
      if (window.ApplePaySession) {
        this.querySelector('.dynamic-button--apple-pay')?.removeAttribute('hidden');
        this.querySelector('.dynamic-button--apple-pay')?.parentElement.setAttribute('data-button-count', 3);
      }
    }

    /**
     * Handles the click event for the faux accelerated checkout buttons
     *
     * @param {Event} event - The event object
     */
    handleClick(event) {
      const button = event.target;
      const buttonType = button.dataset.buttonType;
      const form = button.closest('form');
      const isShopPay = (buttonType === 'shop-pay');

      if (!!(form)) {
        const data = new FormData(form);
        const updates = {
          attributes: {}
        };
        let hasUpdates = false;

        if (this.component === 'product-form') {
          // Personalization functionality
          const personalizationForm = form.querySelector('.js-personalization-form');

          if (!!(personalizationForm)) {
              const personalizationFormData = [];

              personalizationForm.querySelectorAll('input')?.forEach((input) => {
                  personalizationFormData.push({
                      key: input.dataset.key,
                      value: input.value
                  });
              });

              data.append('properties[_personalization]', JSON.stringify(personalizationFormData));
          }

          const uuidInputs = form.querySelectorAll('.js-product-uuid-input');
          const idInput = form.querySelector('[name="id"]');
          let doesNotHaveUuid = true;

          if (!!(idInput) && !!(uuidInputs) && uuidInputs.length > 0) {
            uuidInputs.forEach((input) => {
              if (Number(input.dataset.variantId) === Number(idInput.value)) {
                input.disabled = false; // Enable the input if the variant is the same
                doesNotHaveUuid = false;
              } else {
                input.disabled = true; // Disable the input if the variant is different
              }
            });
          }

          if (doesNotHaveUuid && !!(idInput)) {
            const uuid = window.crypto.randomUUID();

            data.append('properties[_uuid]', uuid);

            // Add element in case customer wants to add more from the same product form
            form.insertAdjacentHTML('beforeend', `<input type="hidden" name="properties[_uuid]" value="${uuid}" data-variant-id="${idInput.value}" class="js-product-uuid-input">`);
          }

          // Add cart attributes if necessary
          const attributeInputs = form.querySelectorAll('[data-attribute]');

          if (!!(attributeInputs) && attributeInputs.length > 0) {
            attributeInputs.forEach((input) => {
              updates.attributes[input.dataset.attribute] = input.value;
              hasUpdates = true;
            });
          }
        }

        data.append('return_to', `/checkout?${isShopPay ? 'payment=shop_pay' : 'skip_shop_pay=true'}`);

        form.insertAdjacentHTML('beforeend', `<input type="hidden" name="return_to" value="/checkout?${isShopPay ? 'payment=shop_pay' : 'skip_shop_pay=true'}">`);

        if (this.component === 'product-form') {
          cartServiceInstance.clearCart()
            .then(() => {
              if (hasUpdates) {
                return cartServiceInstance.updateCart(updates);
              } else {
                return Promise.resolve();
              }
            })
            .then(() => {
              cartAdd(data, isShopPay, true);
            })
            .catch((error) => {
              console.error(error);
            });
        } else if (this.component === 'cart-details') {
          cartAdd(data, isShopPay, true);
        }
      }
    }
  });
}

/**
 * Cart Add-Ons
 */

if (!customElements.get('cart-addon')) {
  customElements.define('cart-addon', class CartAddon extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.input = this.querySelector('input');

      if (!!(this.input)) {
        this.input.addEventListener('change', this.handleChange.bind(this));
      }
    }

    /**
     * Handles the change event for the cart add-on
     *
     * @param {Event} event - The event object
     */
    handleChange(event) {
      let addonKey = '';
      let parentKey = this.input.dataset.parentKey;
      let parentUUID = this.input.dataset.parentUuid;

      disableCartInteraction();

      if (event.currentTarget.hasAttribute('data-key')) {
        addonKey = event.currentTarget.dataset.key;
      }

      if (event.currentTarget.checked && this.input.dataset.parentProperties.startsWith('[')) {
        const parentProperties = Object.fromEntries(JSON.parse(this.input.dataset.parentProperties));

        if (parentProperties.hasOwnProperty('_add_on_variants')) {
          parentProperties['_add_on_variants'] = JSON.parse(parentProperties['_add_on_variants']);

          parentProperties['_add_on_variants'].push({
            'id': this.input.value,
            'add_on_type': this.input.dataset.type,
            'cost': Number(this.input.dataset.price),
          });
        } else {
          parentProperties['_add_on_variants'] = [];

          parentProperties['_add_on_variants'].push({
            'id': this.input.value,
            'add_on_type': this.input.dataset.type,
            'cost': Number(this.input.dataset.price),
          });
        }

        parentProperties['_add_on_variants'] = JSON.stringify(parentProperties['_add_on_variants']);

        cartServiceInstance.changeCart({
            id: parentKey,
            quantity: Number(this.input.dataset.parentQuantity),
            properties: parentProperties,
            sections: sectionsToFetch,
          })
          .then((data) => {
            refreshDrawerFromCartMutation(data, true);
          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        if (addonKey !== '') {
          const idToRemove = this.input.value;

          if (idToRemove !== '') {
            cartServiceInstance.fetchCart()
              .then((data) => {
                for (const item of data.items) {
                  if (
                    item.properties.hasOwnProperty('_uuid') &&
                    item.properties['_uuid'] === parentUUID
                  ) {
                    if (item.properties.hasOwnProperty('_add_on_variants')) {
                      const addOnVariants = JSON.parse(
                        item.properties['_add_on_variants']
                      );

                      if (addOnVariants.length > 0) {
                        addOnVariants.forEach((addOnVariant) => {
                          if (addOnVariant.id === idToRemove) {
                            addOnVariants.splice(
                              addOnVariants.indexOf(addOnVariant),
                              1
                            );
                          }
                        });
                      }

                      if (addOnVariants.length > 0) {
                        item.properties['_add_on_variants'] = JSON.stringify(addOnVariants);
                      } else {
                        delete item.properties['_add_on_variants'];
                      }
                    }

                    return cartServiceInstance.changeCart({
                          id: item.key,
                          quantity: item.quantity,
                          properties: item.properties,
                          sections: sectionsToFetch,
                        });
                  }
                }
              })
              .then((data) => {
                if (data) {
                  refreshDrawerFromCartMutation(data, true);
                } else {
                  fetchCartDrawer(true);
                }
              })
              .catch((error) => {
                console.error(error);
              });
          }
        }
      }
    }
  });
}

/**
 * Cart Discount Code
 */

if (!customElements.get('discount-code-component')) {
  customElements.define('discount-code-component', class CartDiscount extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.DISCOUNT_ALREADY_APPLIED_MESSAGE = this.dataset.alreadyThereMessage;
      this.combinableDiscountPrefixes = this.dataset.combinableDiscountPrefixes.split(',,,');

      if (!Array.isArray(this.combinableDiscountPrefixes)) {
        this.combinableDiscountPrefixes = [this.combinableDiscountPrefixes];
      }

      this.discountCode = this.dataset.discountCode;
      this.discountCodeInput = this.querySelector('.js-cart-discount-code__input');
      this.discountCodeBtn = this.querySelector('.js-cart-discount-code__btn');
      this.discountCodeMessageRemoveButton = this.querySelectorAll('.js-cart-discount-code__message__item__remove-button');
      this.MAX_APPLIED_DISCOUNTS = Number(this.dataset.maxLimit);
      this.MAX_APPLIED_DISCOUNTS_LIMIT_MESSAGE = this.dataset.maxLimitMessage;

      if (!!(this.discountCodeInput)) {
        this.discountCodeInput.addEventListener('keyup', this.applyDiscountCode.bind(this));
      }

      if (!!(this.discountCodeBtn)) {
        this.discountCodeBtn.addEventListener('click', this.applyDiscountCode.bind(this));
      }

      if (!!(this.discountCodeMessageRemoveButton)) {
        this.discountCodeMessageRemoveButton.forEach((button) => {
          button.addEventListener('click', this.removeDiscountCode.bind(this));
        });
      }
    }

    #handleDiscountError(errorMessage, shouldEnableCartInteraction = true) {
        const errorMessageHTML = `<div class="cart-discount-code__message__item cart-discount-code__message__item--is-error"><p>${errorMessage}</p></div>`;

        this.querySelector('.cart-discount-code__message').insertAdjacentHTML('afterbegin', errorMessageHTML);

        if (shouldEnableCartInteraction) {
            enableCartInteraction();
        }
    }

    /**
     * Applies the discount code to the cart
     *
     * @param {Event} event
     */
    applyDiscountCode(event) {
      // Replace trailing commas and multiple commas with single commas in the input value to prevent empty discount codes from being sent
      this.discountCodeInput.value = String(this.discountCodeInput.value ?? '').toUpperCase().replace(/,+/g, ',').replace(/^,|,$/g, '').trim();

      if (event.currentTarget.name === 'discount_code') {
        if (event.key !== 'Enter') {
          return;
        }
      }

      disableCartInteraction();

      const appliedDiscountCodes = (this.discountCode ?? '').split(',').filter(Boolean);

      const enteredDiscountCodes = String(this.discountCodeInput.value).split(',').filter(Boolean);
      const isEveryEnteredDiscountCombinable = enteredDiscountCodes.every((code) => {
        return this.combinableDiscountPrefixes.some((prefix) => code.startsWith(prefix));
      });

      const areAllEnteredCodesAlreadyApplied = enteredDiscountCodes.every((code) => {
        return appliedDiscountCodes.includes(code);
      });
      
      if (areAllEnteredCodesAlreadyApplied && this.discountCode !== '') {
        this.#handleDiscountError(this.DISCOUNT_ALREADY_APPLIED_MESSAGE);
        return;
      }

      const isEveryAppliedDiscountCombinable = appliedDiscountCodes.every((code) => {
        return this.combinableDiscountPrefixes.some((prefix) => code.startsWith(prefix));
      });

      let discountCodesToSend = [...appliedDiscountCodes];

      if (isEveryEnteredDiscountCombinable && isEveryAppliedDiscountCombinable) {
        discountCodesToSend = [...appliedDiscountCodes].concat(enteredDiscountCodes);
      } else {
        discountCodesToSend = [...enteredDiscountCodes];
      }

      discountCodesToSend = [...new Set(discountCodesToSend)];

      if (discountCodesToSend.length > this.MAX_APPLIED_DISCOUNTS) {
        this.#handleDiscountError(this.MAX_APPLIED_DISCOUNTS_LIMIT_MESSAGE);
        return;
      }

      const discountsToSendString = discountCodesToSend.join(',');

      this.sentDiscountCodes = discountsToSendString.split(',');

      cartServiceInstance.updateCart({
        discount: discountsToSendString,
      })
        .then((data) => {
          const appliedDiscountCodes = Object.values(data?.discount_codes ?? {})
              .filter(d => d.applicable)
              .map(d => d.code);
          const sentDiscountCodes = this.sentDiscountCodes;

          const errorDiscountCodes = sentDiscountCodes.map((sentDiscountCode) => {
            if (!appliedDiscountCodes.includes(sentDiscountCode)) {
              return sentDiscountCode;
            }
            return null;
          }).filter(Boolean);

          return cartServiceInstance.updateCart({
            attributes: {
              applied_discount_codes: appliedDiscountCodes.join(','),
              error_discount_codes: errorDiscountCodes.join(','),
            },
            sections: sectionsToFetch,
          });
        })
        .then((data) => {
          refreshDrawerFromCartMutation(data, true);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          enableCartInteraction();
        });
    }

    /**
     * Removes the discount code from the cart
     *
     * @param {Event} event 
     */
    removeDiscountCode(event) {
      let appliedDiscountCodes = this.discountCode.split(',');

      appliedDiscountCodes.splice(appliedDiscountCodes.indexOf(event.currentTarget.dataset.discountCode), 1);
      appliedDiscountCodes = appliedDiscountCodes.join(',');

      disableCartInteraction();

      cartServiceInstance.updateCart({
        discount: appliedDiscountCodes
      })
        .then((data) => {
          const newAppliedDiscountCodes = Object.values(data?.discount_codes ?? {}).map((discount) => {
            if (discount.applicable) {
              return discount.code;
            }
            return null;
          }).filter(Boolean);

          return cartServiceInstance.updateCart({
            attributes: {
              applied_discount_codes: newAppliedDiscountCodes.join(','),
            },
            sections: sectionsToFetch,
          });
        })
        .then((data) => {
          refreshDrawerFromCartMutation(data, true);
        })
        .catch((error) => {
          console.error(error);
          enableCartInteraction();
        });
    }
  });
}

/**
 * Fetches cart updates when the window regains focus
 */

window.addEventListener('focus', () => {
  disableCartInteraction();

  fetchCartDrawer(false);
});

window.addEventListener('pageshow', (event) => {
  // re-fetch the cart drawer fresh, when browser navigation loads
  // a cached version of the page (bfcache) to ensure the cart drawer is up to date
  if (event.persisted) {
    disableCartInteraction();

    fetchCartDrawer(false);
  }
});